import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getBanksList, refundRequest, verifyBankDetails } from '~api/payment';

export const getBanksListRequest = createAsyncThunk(
  'REFUND/getBanksListRequest',
  async (payload) => {
    try {
      const response = await getBanksList(payload);
      return response.data?.data;
    } catch (err) {
      console.log(err);
      throw new Error('Something went wrong!');
    }
  },
);

export const verifyBankDetailsRequest = createAsyncThunk(
  'REFUND/verifyBankDetailsRequest',
  async (payload) => {
    const response = await verifyBankDetails(payload);
    return response.data;
  },
);

export const refundRequestAsync = createAsyncThunk(
  'REFUND/refundRequest',
  async ({ orderId, params }, { rejectWithValue }) => {
    try {
      const response = await refundRequest(orderId, params);
      return response.data;
    } catch (err) {
      if (err.response && err.response.data && err.response.data.data) {
        return rejectWithValue(err.response.data.data);
      } else {
        throw new Error('Something went wrong!');
      }
    }
  },
);

const initialState = {
  listBank: [],
  refundFilled: {
    bankName: '',
    bankCode: '',
    accountNumber: '',
    accountName: '',
  },
  verifyBankDetails: false,
  verifyRefundRequest: false,
  status: {
    getBanksList: 'idle',
    verifyBankDetails: 'idle',
    verifyRefundRequest: 'idle',
  },
};

const refundSlice = createSlice({
  name: 'refund',
  initialState,
  reducers: {
    resetState: () => initialState,
    updateRefundForm: (state, action) => {
      state.refundFilled = {
        ...state.refundFilled,
        ...action.payload,
      };
    },
    setRefundFormField: (state, action) => {
      const { field, value } = action.payload;
      state.refundFilled[field] = value;
    },
    clearRefundForm: (state) => {
      state.refundFilled = {
        bankName: '',
        bankCode: '',
        accountNumber: '',
        accountName: '',
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getBanksListRequest.pending, (state) => {
        state.status.getBanksList = 'loading';
      })
      .addCase(getBanksListRequest.fulfilled, (state, action) => {
        state.status.getBanksList = 'succeeded';
        state.listBank = action.payload || [];
      })
      .addCase(getBanksListRequest.rejected, (state) => {
        state.status.getBanksList = 'failed';
      })
      .addCase(verifyBankDetailsRequest.pending, (state) => {
        state.status.verifyBankDetails = 'loading';
      })
      .addCase(verifyBankDetailsRequest.fulfilled, (state, action) => {
        state.status.verifyBankDetails = 'succeeded';
        state.verifyBankDetails = action.payload.success;
      })
      .addCase(verifyBankDetailsRequest.rejected, (state) => {
        state.status.verifyBankDetails = 'failed';
        state.verifyBankDetails = false;
      })
      .addCase(refundRequestAsync.pending, (state) => {
        state.status.verifyRefundRequest = 'loading';
      })
      .addCase(refundRequestAsync.fulfilled, (state, action) => {
        state.status.verifyRefundRequest = 'succeeded';
        state.verifyRefundRequest = action.payload.success;
      })
      .addCase(refundRequestAsync.rejected, (state) => {
        state.status.verifyRefundRequest = 'failed';
        state.verifyRefundRequest = false;
      });
  },
});

export const {
  resetState,
  updateRefundForm,
  setRefundFormField,
  clearRefundForm,
} = refundSlice.actions;

// Selectors
export const selectRefundState = (state) => state.refund;
export const selectRefundForm = (state) => state.refund.refundFilled;
export const selectBanksList = (state) => state.refund.listBank;
export const selectVerifyBankDetails = (state) =>
  state.refund.verifyBankDetails;
export const selectRefundRequestStatus = (state) =>
  state.refund.verifyRefundRequest;
export const selectRefundStatus = (state) => state.refund.status;

export default refundSlice.reducer;
